type MathSegment =
  | { type: 'plain'; text: string }
  | { type: 'inline'; latex: string }
  | { type: 'block'; latex: string };

function isCharEscaped(s: string, index: number): boolean {
  let backslashes = 0;
  for (let k = index - 1; k >= 0 && s[k] === '\\'; k -= 1) {
    backslashes += 1;
  }
  return backslashes % 2 === 1;
}

/**
 * Splits content on math delimiters: `$$…$$` (block) and `$…$` (inline).
 * Regions inside delimiters are rendered with KaTeX; outside is plain text.
 */
export function parseMathSegments(s: string): MathSegment[] {
  const out: MathSegment[] = [];
  let i = 0;
  const n = s.length;

  let plainBuf = '';

  const flushPlain = (): void => {
    if (plainBuf.length > 0) {
      out.push({ type: 'plain', text: plainBuf });
      plainBuf = '';
    }
  };

  while (i < n) {
    if (s[i] === '$' && i + 1 < n && s[i + 1] === '$') {
      flushPlain();
      i += 2;
      const end = s.indexOf('$$', i);
      if (end < 0) {
        plainBuf += `$$${s.slice(i)}`;
        break;
      }
      out.push({ type: 'block', latex: s.slice(i, end).trim() });
      i = end + 2;
      continue;
    }

    if (s[i] === '$' && (i + 1 >= n || s[i + 1] !== '$')) {
      if (isCharEscaped(s, i)) {
        plainBuf += s[i];
        i += 1;
        continue;
      }
      flushPlain();
      i += 1;
      let j = i;
      let closeIdx = -1;
      while (j < n) {
        if (
          s[j] === '$' &&
          (j + 1 >= n || s[j + 1] !== '$') &&
          !isCharEscaped(s, j)
        ) {
          closeIdx = j;
          break;
        }
        j += 1;
      }
      if (closeIdx < 0) {
        plainBuf += `$${s.slice(i)}`;
        break;
      }
      out.push({ type: 'inline', latex: s.slice(i, closeIdx).trim() });
      i = closeIdx + 1;
      continue;
    }

    plainBuf += s[i];
    i += 1;
  }

  flushPlain();
  return out;
}
